import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";

import validationSchemas from "../../../utils/validationSchemas";
import FieldsMapper from "../../fieldsMapper/FieldsMapper";
import FileUploadSection from "../../fileUploadSection/FileUploadSection";
import PreviewTable from "../../previewTable/PreviewTable";
import WizardModal from "../../wizard/WizardModal/WizardModal";
import Section from "../../wizard/Section";

import TaskActions from "../../../enums/TaskActions";
import dataService from "../../../features/Application/services/dataServices/typedDataService";
import backgroundTasksEventsEmitter from "../../../features/BackgroundTasks/events/backgroundTasksEventsEmitter";
import importUsersTasks from "../../../features/People/Users/UsersOverview/backgroundTasks/importUsersTasks";
import fileUtils, { type AcceptCancelationHandler, type OnUploadProgressHandler } from "../../../utils/fileUtils";

import type { FileTask } from "../../../features/BackgroundTasks/taskPool";

import styles from "./csvRemoveUsersModal.module.scss";
import { type FileUploadState } from "../../../interfaces/FileUploadState";
import { type BackgroundTaskHandlers } from "../../../interfaces/BackgroundTaskHandlers";
import { type MappedField, type MapToField } from "interfaces/MappedField";
import type { StartUsersImportResponse } from "../../../features/People/types";

enum RemoveUsersSteps {
  UploadFile = 0,
  MapFields,
  Preview,
}

export interface Props extends FileUploadState, BackgroundTaskHandlers {
  accountId: number;
  groupId: number;
  modalTitle?: string;
  notifyLabelText?: string;
  showModal: boolean;
  actorId: number;
  onClose: () => void;
  resetUsersFileUpload: () => void;
  cancelUsersFileUpload: () => void;
  resetUsersImportPreviewData: () => void;
  updateUsersFileUploadProgress: () => void;
  getUploadedFileColumns: (fileName: string) => void;
  showGeneralLoaderWithTimeout: (timeout: number, cb: () => void) => void;
  uploadedUsersFileColumns: { isLoading: boolean; items: string[] };
  usersImportPreviewData: { isLoading: boolean; items: any; itemsCount: number };
  uploadUsersFile: (
    accountId: number,
    actorId: number,
    contentType: string,
    file: File,
    onUploadProgressHandler: OnUploadProgressHandler,
    acceptCancellationHandler: AcceptCancelationHandler,
  ) => void;

  getUsersImportPreviewData: (
    fileName: string,
    previewItemsCount: number,
    arr: { mapFrom: string; mapTo: string }[],
  ) => void;
}

const CsvRemoveUsersModal: React.FC<Props> = ({
  groupId,
  showModal,
  actorId,
  isFileUploading,
  uploadProgress,
  uploadError,
  uploadUsersFile,
  updateUsersFileUploadProgress,
  uploadedUsersFileColumns,
  usersImportPreviewData,
  accountId,
  onClose,
  resetUsersFileUpload,
  resetUsersImportPreviewData,
  onFileDisposed,
  cancelUsersFileUpload,
  getUploadedFileColumns,
  getUsersImportPreviewData,
  addBackgroundTask,
  removeBackgroundTask,
  cancelBackgroundTask,
  showGeneralLoaderWithTimeout,
  backgroundTasks,
  droppedFile,
}) => {
  const [mappedFields, setMappedFields] = useState<MappedField[]>([]);
  const [userImport, setUserImport] = useState<StartUsersImportResponse | undefined>();
  const [customFields, setCustomFields] = useState<MapToField[]>([]);
  const [fileName, setFileName] = useState("");
  const [originalFileName, setOriginalFileName] = useState("");
  const uploadCancellationHandler = useRef<{ abort: () => void } | null>(null);

  const droppedFiles = useMemo(() => (droppedFile ? [droppedFile] : []), [droppedFile]);

  const uploadStatus = useMemo(
    () =>
      uploadProgress !== 100 ||
      !!uploadError ||
      isFileUploading ||
      uploadedUsersFileColumns.isLoading ||
      uploadedUsersFileColumns.items.length === 0,
    [uploadProgress, uploadError, isFileUploading, uploadedUsersFileColumns],
  );

  const mappedFieldsForApiRequest = useMemo(
    () => mappedFields.filter((f) => f.mapFrom && f.mapTo).map(({ mapFrom, mapTo }) => ({ mapFrom, mapTo })),
    [mappedFields],
  );

  const areAllRequiredFieldsMapped = useCallback(
    (mapToFields: MapToField[]) =>
      mappedFields.length > 0 &&
      !mappedFields.some((field) =>
        mapToFields.some((req) => req.isRequired && field.mapTo === req.name && !field.mapFrom),
      ),
    [mappedFields],
  );

  const handleMappingChange = useCallback((newMappedFields: MappedField[]) => {
    setMappedFields(newMappedFields);
  }, []);

  const handleCustomFieldChange = useCallback((newCustomFields: MapToField[]) => {
    setCustomFields(newCustomFields);
  }, []);

  const handleFileChange = useCallback(
    (newFileName: string, original: string) => {
      setMappedFields([]);
      setFileName(newFileName);
      setOriginalFileName(original);
      setCustomFields([]);
      getUploadedFileColumns(newFileName);
    },
    [getUploadedFileColumns],
  );

  const handleCloseModal = useCallback(() => {
    if (isFileUploading) {
      cancelUsersFileUpload();
      uploadCancellationHandler.current?.abort();
    }
    onClose();
    resetUsersFileUpload();
    resetUsersImportPreviewData();
    onFileDisposed();
  }, [
    isFileUploading,
    cancelUsersFileUpload,
    onClose,
    resetUsersFileUpload,
    resetUsersImportPreviewData,
    onFileDisposed,
  ]);

  useEffect(() => {
    if (userImport) {
      backgroundTasksEventsEmitter.updateHandlerOnce(TaskActions.close, userImport.id, removeBackgroundTask);
      backgroundTasksEventsEmitter.updateHandlerOnce(TaskActions.cancel, userImport.id, async (taskId: number) => {
        await dataService.users.cancelUsersImportTask(userImport);
        cancelBackgroundTask(taskId);
      });
      backgroundTasksEventsEmitter.updateHandler(TaskActions.click, userImport.id, (taskId: number) => {
        const task = backgroundTasks[taskId] as FileTask;
        if (task?.fileUri) fileUtils.downloadFile(task.fileUri);
      });
    }
  }, [backgroundTasks, cancelBackgroundTask, removeBackgroundTask, userImport]);

  const handleFinish = async () => {
    showGeneralLoaderWithTimeout(3000, async () => {
      const data = await dataService.users.startUsersRemoveImport(fileName, groupId, mappedFieldsForApiRequest);
      const task = importUsersTasks.InProgress(data.id, originalFileName);
      addBackgroundTask(task);
      setUserImport(data);
    });
    handleCloseModal();
  };

  const stepActions = useMemo(
    () => ({
      [RemoveUsersSteps.UploadFile]: {
        onNext: onFileDisposed,
      },
      [RemoveUsersSteps.MapFields]: {
        onNext: () => getUsersImportPreviewData(fileName, 11, mappedFieldsForApiRequest),
      },
      [RemoveUsersSteps.Preview]: {
        onPrevious: resetUsersImportPreviewData,
      },
    }),
    [fileName, mappedFieldsForApiRequest, getUsersImportPreviewData, onFileDisposed, resetUsersImportPreviewData],
  );

  const acceptCancellationHandler = useCallback((handler: { abort: () => void }) => {
    uploadCancellationHandler.current = handler;
  }, []);

  if (!showModal) return null;

  return (
    <WizardModal
      title="Remove Members via CSV"
      isOpen={showModal}
      onClose={handleCloseModal}
      stepActions={stepActions}
      onFinish={handleFinish}
    >
      <Section label="Upload File" required isLocked={uploadStatus}>
        <FileUploadSection
          accountId={accountId}
          actorId={actorId}
          isUploading={isFileUploading}
          uploadProgress={uploadProgress}
          uploadError={uploadError}
          uploadFile={uploadUsersFile}
          initialFiles={droppedFiles}
          cancelFileUpload={cancelUsersFileUpload}
          updateProgress={updateUsersFileUploadProgress}
          acceptFileFormat=".csv"
          label="File (.csv)"
          propertyName="files"
          validationSchema={validationSchemas.usersFileUpload}
          acceptCancellationHandler={acceptCancellationHandler}
          onFileChange={handleFileChange}
        >
          <p className={styles.label}>
            Download the{" "}
            <span className={styles.link} onClick={dataService.users.getImportFileTemplate} title="Download">
              template
            </span>{" "}
            to ensure your file is correctly formatted, then upload the list of users you want to <b>remove</b> from the
            group.
          </p>
        </FileUploadSection>
        <NoteBlock />
      </Section>

      <Section label="Map Fields" required isLocked={!areAllRequiredFieldsMapped(MAP_TO_FIELDS)}>
        <FieldsMapper
          header="Match the email field from your CSV to the corresponding field in our system. Only the email field is required to proceed with deletion."
          onlyRequiredFields
          mapFromColumnTitle="CSV Column"
          mapToColumnTitle="Field Name"
          mapFromFields={uploadedUsersFileColumns.items}
          mapToFields={MAP_TO_FIELDS}
          mappedFields={mappedFields}
          customFields={customFields}
          onMappingChange={handleMappingChange}
          onCustomFieldChange={handleCustomFieldChange}
          allowCustomFields
        />
      </Section>

      <Section
        label="Preview"
        isLocked={usersImportPreviewData.isLoading}
        extraAction={<NoteBlock />}
        finishButton="Remove Users"
      >
        <PreviewTable
          isGroup
          actionText="removed"
          titles={TITLES_FOR_PREVIEW}
          rowDataSequenceAndOptions={PREVIEW_ROW_DATA_SEQUENCE_AND_OPTIONS}
          items={usersImportPreviewData.items}
          withGradient={usersImportPreviewData.itemsCount > PREVIEW_GRADIENT_THRESHOLD}
          itemsAmount={usersImportPreviewData.itemsCount}
        />
      </Section>
    </WizardModal>
  );
};

const TITLES_FOR_PREVIEW: string[] = ["Email"];
const MAP_TO_FIELDS: MapToField[] = [{ name: "Email", isRequired: true }];
const PREVIEW_ROW_DATA_SEQUENCE_AND_OPTIONS = [{ name: "email", clamp: 1 }];
const PREVIEW_GRADIENT_THRESHOLD = 11;

const NoteBlock: React.FC = () => (
  <p className={styles.noteBlock}>
    <b>Note:</b> Removing users from this group will not delete them from the system. Their accounts and any content
    licenses they’ve used will remain unchanged.
  </p>
);

export default CsvRemoveUsersModal;
